import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FilterChips } from "@/components/ColumnHead";
import { Icon } from "@/components/Icon";
import { Later } from "@/components/Later";
import { Failure, NotFound } from "@/features/shell/Problem";
import { WarehouseOrderCards, WarehouseOrdersTable, useWarehouseOrderColumns } from "@/features/warehouseOrders/WarehouseOrdersTable";
import { ApiError } from "@/lib/api";
import { nextSort, type SortState } from "@/lib/columnFilters";
import {
  WAREHOUSE_ORDER_COLUMNS,
  WAREHOUSE_ORDER_VIEWS,
  columnFilterWarehouseOrders,
  countWarehouseOrders,
  filterWarehouseOrders,
  sortWarehouseOrders,
  warehouseOrdersQuery,
  type WarehouseOrderColumn,
  type WarehouseOrderView,
} from "@/lib/warehouseOrder";
import { PHONE, useMedia } from "@/lib/useMedia";

// Magazijnorders (26-10-2026): the uitleveringen and aanleveringen of the last ninety days, newest first, also the ones
// collected or brought, which have no shipment and so are not on Zendingen. Only for a customer with Voorraad in het
// portaal, as Order invoeren offers them only then. Filter and search are in the address, and since 26-10-2026 the sort
// and a filter per column (sort, order, filter.<column>), applied here in the browser as the API does on Zendingen.

interface WarehouseOrdersSearch {
  view?: Exclude<WarehouseOrderView, "all">;
  q?: string;
  sort?: WarehouseOrderColumn;
  order?: "desc";
  [filter: `filter.${string}`]: string | undefined;
}

function text(value: unknown): string | undefined {
  if (typeof value === "number") return String(value);
  return typeof value === "string" && value.trim() ? value : undefined;
}

export const Route = createFileRoute("/_portal/warehouse-orders")({
  validateSearch: (search: Record<string, unknown>): WarehouseOrdersSearch => {
    const view = WAREHOUSE_ORDER_VIEWS.find((v) => v !== "all" && v === search.view) as WarehouseOrdersSearch["view"];
    const sort = WAREHOUSE_ORDER_COLUMNS.find((column) => column === search.sort);
    const filters = Object.fromEntries(
      WAREHOUSE_ORDER_COLUMNS.map((column) => [`filter.${column}`, text(search[`filter.${column}`])]).filter(([, value]) => value),
    );
    return { view, q: text(search.q), sort, order: sort && search.order === "desc" ? "desc" : undefined, ...filters };
  },
  component: WarehouseOrdersScreen,
});

function WarehouseOrdersScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const phone = useMedia(PHONE);
  const list = useQuery(warehouseOrdersQuery);

  // Search as you type, in the address a moment after the last key.
  const [typed, setTyped] = useState(search.q ?? "");
  useEffect(() => {
    setTyped((current) => (current.trim() === (search.q ?? "") ? current : (search.q ?? "")));
  }, [search.q]);
  useEffect(() => {
    const q = typed.trim() || undefined;
    if (q === search.q) return;
    const handle = window.setTimeout(() => void navigate({ search: (prev) => ({ ...prev, q }), replace: true }), 300);
    return () => window.clearTimeout(handle);
  }, [typed, search.q, navigate]);

  const filters: Partial<Record<WarehouseOrderColumn, string>> = Object.fromEntries(
    WAREHOUSE_ORDER_COLUMNS.map((column) => [column, search[`filter.${column}`]]).filter(([, value]) => value),
  );
  const sort: SortState = { sort: search.sort, order: search.sort ? (search.order === "desc" ? "desc" : "asc") : undefined };
  const columns = useWarehouseOrderColumns(list.data ?? []);
  const setSort = (column: string) => {
    const next = nextSort(sort, column);
    void navigate({
      search: (prev) => ({ ...prev, sort: next.sort as WarehouseOrderColumn | undefined, order: next.order === "desc" ? "desc" : undefined }),
      replace: true,
    });
  };
  const setFilter = (column: string, value: string) =>
    void navigate({ search: (prev) => ({ ...prev, [`filter.${column}`]: value.trim() || undefined }), replace: true });
  const clearFilters = () =>
    void navigate({
      search: (prev) => ({ ...prev, ...Object.fromEntries(WAREHOUSE_ORDER_COLUMNS.map((column) => [`filter.${column}`, undefined])) }),
      replace: true,
    });
  const filtered = Object.keys(filters).length > 0;

  if (list.error instanceof ApiError && list.error.status === 404) return <NotFound />;
  if (list.error) return <Failure error={list.error} onRetry={() => void list.refetch()} />;

  const view: WarehouseOrderView = search.view ?? "all";
  const orders = list.data ?? [];
  // The counts on the buttons count the search and the column filters: what each button would show.
  const found = columnFilterWarehouseOrders(filterWarehouseOrders(orders, { view: "all", q: search.q }), filters);
  const counts = countWarehouseOrders(found);
  const shown = sortWarehouseOrders(filterWarehouseOrders(found, { view }), sort);

  let content;
  if (!list.data) {
    content = (
      <Later>
        <p className="t-body-sm" style={{ color: "var(--text-secondary)" }}>
          {t("warehouseOrders.loading")}
        </p>
      </Later>
    );
  } else if (orders.length === 0) {
    content = (
      <div className="wl-empty" data-screen-label="Portaal Magazijnorders leeg">
        <div className="wl-empty__icon">
          <Icon name="building" size={20} />
        </div>
        <div className="wl-empty__title">{t("warehouseOrders.emptyTitle")}</div>
        <p className="wl-empty__text">{t("warehouseOrders.emptyText")}</p>
        <Link className="wl-btn wl-btn--secondary" to="/warehouse-order" search={{ kind: "outbound" }}>
          {t("nav.order")}
        </Link>
      </div>
    );
  } else if (shown.length === 0) {
    content = (
      <div className="wl-empty" data-screen-label="Portaal Magazijnorders niets gevonden">
        <div className="wl-empty__icon">
          <Icon name="search" size={20} />
        </div>
        <div className="wl-empty__title">
          {search.q ? t("warehouseOrders.notFound", { q: search.q }) : filtered ? t("warehouseOrders.notFiltered") : t(`warehouseOrders.none.${view}`)}
        </div>
        <button
          type="button"
          className="wl-btn wl-btn--secondary"
          onClick={() => {
            setTyped("");
            void navigate({ search: {} });
          }}
        >
          {t("warehouseOrders.showAll")}
        </button>
      </div>
    );
  } else if (phone) {
    content = <WarehouseOrderCards orders={shown} />;
  } else {
    content = (
      <div className="wl-table-wrap">
        <WarehouseOrdersTable orders={shown} columns={columns} sort={sort} onSort={setSort} filters={filters} onFilter={setFilter} />
      </div>
    );
  }

  return (
    <div className="wl-portal-split">
      <div className="wl-portal-page" data-screen-label="Portaal Magazijnorders">
        <div className="wl-portal-head">
          <div className="wl-vstack" style={{ gap: "var(--space-1)" }}>
            <h1 className="t-h2">{t("nav.warehouseOrders")}</h1>
            <span className="t-body-sm" style={{ color: "var(--text-secondary)" }}>
              {t("warehouseOrders.lead")}
            </span>
          </div>
        </div>

        <div className="wl-portal-toolbar">
          <div className="wl-segment" role="group" aria-label={t("warehouseOrders.views")}>
            {WAREHOUSE_ORDER_VIEWS.map((v) => (
              <button
                key={v}
                type="button"
                className={v === view ? "wl-segment__btn is-active" : "wl-segment__btn"}
                aria-pressed={v === view}
                disabled={!list.data}
                onClick={() => void navigate({ search: (prev) => ({ ...prev, view: v === "all" ? undefined : v }) })}
              >
                {t(`warehouseOrders.view.${v}`)}
                {list.data && <span className="count">{counts[v]}</span>}
              </button>
            ))}
          </div>
          <label className="wl-input-group">
            <span className="wl-input-group__icon">
              <Icon name="search" />
            </span>
            <input
              type="search"
              value={typed}
              disabled={!list.data}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={phone ? t("warehouseOrders.searchShort") : t("warehouseOrders.search")}
              aria-label={t("warehouseOrders.search")}
            />
          </label>
        </div>

        <FilterChips columns={columns} values={filters} onFilter={setFilter} onClear={clearFilters} />

        {content}
      </div>
    </div>
  );
}
